import React, { useEffect, useState } from 'react';
import { useReveal, useBodyScrollLock } from '../hooks';
import { IconClose, IconChevronLeft, IconChevronRight, IconMinus, IconPlus, IconStar, IconShield, IconClock, IconCamera } from '../icons';
import { imageUrl } from '../api';

/* -------------------------------- reveal --------------------------------- */

/** Fades its children up into view the first time they scroll into the page */
export const Reveal = ({ children, as: Tag = 'div', className = '', delay = 0, ...rest }) => {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`site-reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/* -------------------------------- toasts --------------------------------- */

const listeners = new Set();
let toastId = 0;

/** toast('Saved to your wishlist') from anywhere; ToastHost draws it */
export const toast = (message, { tone = 'default', duration = 2800 } = {}) => {
  const item = { id: ++toastId, message, tone, duration };
  listeners.forEach((listener) => listener(item));
};

export const ToastHost = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const listener = (item) => {
      setItems((current) => [...current, item]);
      setTimeout(() => setItems((current) => current.filter((entry) => entry.id !== item.id)), item.duration);
    };
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="site-toasts" role="status" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className={`site-toast site-toast-${item.tone}`}>{item.message}</div>
      ))}
    </div>
  );
};

/* ------------------------------- skeletons ------------------------------- */

export const Skeleton = ({ className = '', style }) => <span className={`site-skeleton ${className}`} style={style} />;

export const CardSkeleton = ({ layout = 'grid' }) => (
  <div className={`gb-card gb-card-${layout} gb-card-skeleton`} aria-hidden="true">
    <Skeleton className="gb-card-media" />
    <div className="gb-card-body">
      <Skeleton style={{ width: '70%', height: 20 }} />
      <Skeleton style={{ width: '45%', height: 14 }} />
      <Skeleton style={{ width: '55%', height: 14, marginTop: 10 }} />
    </div>
    <div className="gb-card-side">
      <Skeleton style={{ width: 90, height: 26 }} />
      <Skeleton style={{ width: 100, height: 34 }} />
    </div>
  </div>
);

/* ------------------------------- feedback -------------------------------- */

export const EmptyState = ({ icon, title, text, action }) => (
  <div className="site-empty">
    {icon && <span className="site-empty-icon">{icon}</span>}
    <h3>{title}</h3>
    {text && <p>{text}</p>}
    {action}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="site-empty site-empty-error">
    <h3>Something went wrong</h3>
    <p>{message}</p>
    {onRetry && (
      <button type="button" className="lp-btn lp-btn-primary" onClick={onRetry}>Try again</button>
    )}
  </div>
);

/* --------------------------------- bits ---------------------------------- */

export const Badge = ({ tone = 'neutral', icon, children }) => (
  <span className={`site-badge site-badge-${tone}`}>
    {icon}
    {children}
  </span>
);

export const VerifiedBadge = () => <Badge tone="verified" icon={<IconShield size={12} />}>Verified</Badge>;
export const FeaturedBadge = () => <Badge tone="featured" icon={<IconStar size={12} />}>Featured</Badge>;
export const HourlyBadge = () => <Badge tone="hourly" icon={<IconClock size={12} />}>Hourly stay available</Badge>;

export const Stars = ({ value, size = 14 }) => {
  const count = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
  if (!count) return null;
  return (
    <span className="site-stars" aria-label={`${count} star`}>
      {Array.from({ length: count }).map((_, i) => <IconStar key={i} size={size} fill="currentColor" />)}
    </span>
  );
};

export const Price = ({ amount, cutAmount, currency, unit, size = 'md' }) => {
  const symbol = !currency || currency === 'INR' ? '₹' : currency;
  if (amount === null || amount === undefined) return null;
  return (
    <span className={`site-price site-price-${size}`}>
      {cutAmount != null && Number(cutAmount) > Number(amount) && (
        <s>{symbol}{Math.round(cutAmount).toLocaleString('en-IN')}</s>
      )}
      <b>{symbol}{Math.round(amount).toLocaleString('en-IN')}</b>
      {unit && <small>{unit}</small>}
    </span>
  );
};

/** - 2 + control with bounds */
export const Stepper = ({ value, onChange, min = 0, max = 99, label }) => (
  <div className="site-stepper" role="group" aria-label={label}>
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label={`Fewer ${label || ''}`}>
      <IconMinus size={16} />
    </button>
    <span>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label={`More ${label || ''}`}>
      <IconPlus size={16} />
    </button>
  </div>
);

export const Chip = ({ active, onClick, children, icon }) => (
  <button type="button" className={`site-chip ${active ? 'is-active' : ''}`} onClick={onClick} aria-pressed={active}>
    {icon}
    {children}
  </button>
);

export const SectionHead = ({ eyebrow, title, text, action }) => (
  <div className="site-section-head">
    <div>
      {eyebrow && <span className="lp-eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
    {action}
  </div>
);

/* --------------------------------- modal --------------------------------- */

export const Sheet = ({ open, onClose, title, children, size = 'md', hideHeader = false }) => {
  useBodyScrollLock(open);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="site-sheet-backdrop" onClick={onClose} role="presentation">
      <div className={`site-sheet site-sheet-${size}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {!hideHeader && (
          <div className="site-sheet-head">
            <h3>{title}</h3>
            <button type="button" className="site-icon-btn" onClick={onClose} aria-label="Close"><IconClose size={20} /></button>
          </div>
        )}
        <div className="site-sheet-body">{children}</div>
      </div>
    </div>
  );
};

/* -------------------------------- gallery -------------------------------- */

/** Photo grid with a lightbox; falls back to a single hero when few photos */
export const Gallery = ({ images = [], alt = '' }) => {
  const [open, setOpen] = useState(null);
  useBodyScrollLock(open !== null);
  const photos = (images || []).filter(Boolean);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(null);
      if (event.key === 'ArrowRight') setOpen((i) => (i + 1) % photos.length);
      if (event.key === 'ArrowLeft') setOpen((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, photos.length]);

  if (photos.length === 0) {
    return (
      <div className="site-gallery site-gallery-empty">
        <IconCamera size={36} />
        <span>No photos yet</span>
      </div>
    );
  }

  const shown = photos.slice(0, 5);
  return (
    <>
      <div className={`site-gallery site-gallery-${Math.min(shown.length, 5)}`}>
        {shown.map((src, index) => (
          <button type="button" key={`${src}-${index}`} className="site-gallery-item" onClick={() => setOpen(index)}>
            <img src={imageUrl(src)} alt={`${alt} ${index + 1}`} loading={index === 0 ? 'eager' : 'lazy'} />
            {index === shown.length - 1 && photos.length > shown.length && (
              <span className="site-gallery-more">+{photos.length - shown.length} photos</span>
            )}
          </button>
        ))}
      </div>

      {open !== null && (
        <div className="site-lightbox" onClick={() => setOpen(null)} role="dialog" aria-modal="true" aria-label="Photos">
          <button type="button" className="site-lightbox-close" onClick={() => setOpen(null)} aria-label="Close"><IconClose size={22} /></button>
          {photos.length > 1 && (
            <button type="button" className="site-lightbox-nav is-prev" onClick={(e) => { e.stopPropagation(); setOpen((i) => (i - 1 + photos.length) % photos.length); }} aria-label="Previous">
              <IconChevronLeft size={26} />
            </button>
          )}
          <img src={imageUrl(photos[open])} alt={`${alt} ${open + 1}`} onClick={(e) => e.stopPropagation()} />
          {photos.length > 1 && (
            <button type="button" className="site-lightbox-nav is-next" onClick={(e) => { e.stopPropagation(); setOpen((i) => (i + 1) % photos.length); }} aria-label="Next">
              <IconChevronRight size={26} />
            </button>
          )}
          <span className="site-lightbox-count">{open + 1} / {photos.length}</span>
        </div>
      )}
    </>
  );
};

/** Simple horizontal scroller with arrows on desktop */
export const Scroller = ({ children, className = '' }) => {
  const ref = React.useRef(null);
  const scrollBy = (dir) => {
    const node = ref.current;
    if (!node) return;
    node.scrollBy({ left: dir * Math.max(240, node.clientWidth * 0.8), behavior: 'smooth' });
  };
  return (
    <div className={`site-scroller-wrap ${className}`}>
      <button type="button" className="site-scroller-btn is-prev" onClick={() => scrollBy(-1)} aria-label="Scroll left"><IconChevronLeft size={20} /></button>
      <div className="site-scroller" ref={ref}>{children}</div>
      <button type="button" className="site-scroller-btn is-next" onClick={() => scrollBy(1)} aria-label="Scroll right"><IconChevronRight size={20} /></button>
    </div>
  );
};
